import MarketplaceListingCard from "./marketplace/MarketplaceListingCard";
import { normalizeEngineForCard } from "@/utils/marketplaceCardNormalizer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ArrowRight, Loader2, Cog, Search, Crown } from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import EngineListingDetailModal from "./engines/EngineListingDetailModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFeaturedEngineListings } from "@/hooks/useFeaturedEngineListings";
import { supabase } from "@/integrations/supabase/client";
import { EngineListingCardSkeleton } from './engines/EngineListingCardSkeleton';
import { preloadOptimizedImages } from "@/utils/imageOptimization";

const FeaturedEngines = () => {
  const isMobile = useIsMobile();
  const [selectedEngine, setSelectedEngine] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { data: featuredEngines = [], isLoading: enginesLoading } = useFeaturedEngineListings({
    featured: true,
    status: 'active',
    listingTypes: ['engine_sell', 'engine_rent'],
    limit: 20
  });
  
  const { data: featuredRequests = [] } = useFeaturedEngineListings({
    featured: true,
    status: 'active',
    listingTypes: ['engine_buy', 'engine_rent_request'],
    limit: 20
  });

  // Preload featured images for better LCP
  useEffect(() => {
    if (featuredEngines.length > 0) {
      const imageUrls = featuredEngines
        .slice(0, 6)
        .map(l => l.heroImage || l.photos?.[0])
        .filter((url): url is string => !!url);
      preloadOptimizedImages(imageUrls, 'medium');
    }
  }, [featuredEngines]);

  // Fetch seller profiles with TanStack Query for caching & dedup
  const allListings = useMemo(() => [...featuredEngines, ...featuredRequests], [featuredEngines, featuredRequests]);
  const userIds = useMemo(() => [...new Set(allListings.map(l => l.user_id))].filter(Boolean).sort(), [allListings]);
  
  const { data: sellerProfiles = {} } = useQuery({
    queryKey: ['seller-profiles', userIds.join(',')],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .in('user_id', userIds);
      return (data || []).reduce((acc, profile) => {
        acc[profile.user_id] = profile;
        return acc;
      }, {} as Record<string, any>);
    },
    staleTime: 5 * 60 * 1000,
  });

  const handleEngineClick = (engine: any) => {
    setSelectedEngine(engine);
    setIsModalOpen(true);
  };

  if (enginesLoading) {
    return (
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <EngineListingCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-4 pb-8 md:py-20 bg-background">
      {/* Section Headers - inside container */}
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header - Desktop only with decorative lines */}
        <div className="hidden md:block text-center mb-20 space-y-6">
          <div className="flex items-center justify-center gap-6">
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-[hsl(45,93%,47%)] to-[hsl(45,93%,47%)]" />
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[hsl(45,93%,47%)]/10 to-[hsl(45,93%,60%)]/10 border border-[hsl(45,93%,47%)]/30 text-sm font-semibold text-[hsl(45,93%,40%)] tracking-wide">
              <Crown className="w-4 h-4" />
              Premium Marketplace
            </div>
            <div className="h-px w-24 bg-gradient-to-l from-transparent via-[hsl(45,93%,47%)] to-[hsl(45,93%,47%)]" />
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold tracking-tight">
            <span className="bg-gradient-to-r from-[hsl(205,87%,21%)] via-[hsl(205,87%,30%)] to-[hsl(205,87%,21%)] bg-clip-text text-transparent">
              Featured Engines
            </span>
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Discover premium helicopter engines and connect with serious buyers
          </p>
        </div>
        
        {/* Mobile Section Header */}
        <div className="md:hidden text-center mb-6 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 text-sm font-medium text-primary">
            <Cog className="w-4 h-4" />
            Premium Engine Marketplace
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold">
            <span className="bg-gradient-to-r from-primary via-primary to-primary bg-clip-text text-transparent">
              Featured Engines
            </span>
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
            Discover premium helicopter engines and connect with serious buyers
          </p>
        </div>

        {/* Mobile Featured Sell Offerings */}
        {isMobile && (
          <div className="relative">
            {/* Mobile Section Header - matches desktop divider style */}
            <div className="text-center mb-4 space-y-2">
              <div className="relative flex items-center justify-center w-full">
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />
                <div className="relative z-10 px-5 py-2 bg-background rounded-full border border-emerald-200 dark:border-emerald-800 shadow-sm backdrop-blur-sm flex items-center gap-2">
                  <Cog className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <h3 className="text-base font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    Featured Engine Sales
                  </h3>
                  {featuredEngines.length > 0 && (
                    <Badge variant="secondary" className="text-xs ml-1">
                      {featuredEngines.length}
                    </Badge>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Swipe to browse</p>
            </div>

            {featuredEngines.length > 0 ? (
              <Carousel opts={{ align: "start", loop: true }} className="w-full">
                <CarouselContent className="-ml-2">
                  {featuredEngines.map((engine) => (
                    <CarouselItem key={engine.id} className="pl-2 basis-1/2 shrink-0 h-auto">
                      <div className="px-1 h-full">
                        <MarketplaceListingCard
                          mobileCompact={true}
                          {...normalizeEngineForCard(engine)}
                          onClick={() => handleEngineClick(engine)}
                          onViewDetails={() => handleEngineClick(engine)}
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                <CarouselNext className="right-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
              </Carousel>
            ) : (
              <div className="text-center py-10 md:py-16">
                <Cog className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No featured engines yet</p>
              </div>
            )}
            
            <div className="text-center mt-6">
              <Link to="/marketplace?asset=engine&market=sell&type=engine_sell,engine_rent">
                <Button variant="luxury" size="sm" className="gap-2">
                  View All Sales
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Mobile Featured Buy Requests */}
            <div className="relative mt-6">
              {/* Mobile Section Header - matches desktop divider style */}
              <div className="text-center mb-4 space-y-2">
                <div className="relative flex items-center justify-center w-full">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
                  <div className="relative z-10 px-5 py-2 bg-background rounded-full border border-amber-200 dark:border-amber-800 shadow-sm backdrop-blur-sm flex items-center gap-2">
                    <Search className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <h3 className="text-base font-semibold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                      Featured Buy Requests
                    </h3>
                    {featuredRequests.length > 0 && (
                      <Badge variant="secondary" className="text-xs ml-1">
                        {featuredRequests.length}
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Swipe to browse</p>
              </div>

              {featuredRequests.length > 0 ? (
                <Carousel opts={{ align: "start", loop: true }} className="w-full">
                  <CarouselContent className="-ml-2">
                    {featuredRequests.map((request) => (
                      <CarouselItem key={request.id} className="pl-2 basis-1/2 shrink-0 h-auto">
                        <div className="px-1 h-full">
                          <MarketplaceListingCard
                          mobileCompact={true}
                            {...normalizeEngineForCard(request)}
                            onClick={() => handleEngineClick(request)}
                            onViewDetails={() => handleEngineClick(request)}
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                  <CarouselNext className="right-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                </Carousel>
              ) : (
                <div className="text-center py-10 md:py-16">
                  <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No featured buy requests yet</p>
                </div>
              )}
              
              <div className="text-center mt-6">
                <Link to="/marketplace?asset=engine&market=buy&type=engine_buy,engine_rent_request">
                  <Button variant="outline" size="sm" className="gap-2">
                    View All Requests
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Featured Sell Offerings - OUTSIDE container for full width */}
      {!isMobile && (
        <div className="w-full px-8 lg:px-16 max-w-[2000px] mx-auto mb-16">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/80 via-teal-50/60 to-emerald-50/80 dark:from-emerald-950/20 dark:via-teal-950/10 dark:to-emerald-950/20 rounded-3xl -z-10" />
            <div className="p-8 md:p-12">
              <div className="text-center mb-10 space-y-4">
                <div className="relative flex items-center justify-center w-full">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />
                  <div className="relative z-10 px-6 py-2.5 bg-background rounded-full border border-emerald-200 dark:border-emerald-800 shadow-sm backdrop-blur-sm">
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                      Featured Sell Offerings
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Premium engines with enhanced visibility</p>
              </div>
              {featuredEngines.length > 0 ? (
                <>
                  <Carousel opts={{ align: "center", loop: true, containScroll: "trimSnaps" }} className="w-full">
                    <CarouselContent className="-ml-6">
                      {featuredEngines.map((engine) => (
                        <CarouselItem key={engine.id} className="pl-6 basis-[360px] shrink-0">
                          <MarketplaceListingCard {...normalizeEngineForCard(engine)} onClick={() => handleEngineClick(engine)} onViewDetails={() => handleEngineClick(engine)} />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="left-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-emerald-500" />
                    <CarouselNext className="right-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-emerald-500" />
                  </Carousel>
                  <div className="text-center mt-8">
                    <Link to="/marketplace?asset=engine&market=sell&type=engine_sell,engine_rent">
                      <Button variant="luxury" className="gap-2 shadow-lg">View All Sales<ArrowRight className="w-4 h-4" /></Button>
                    </Link>
                  </div>
                </>
              ) : (
                <div className="text-center py-10 md:py-16">
                  <Cog className="w-20 h-20 mx-auto mb-4 text-amber-600 dark:text-amber-400" />
                  <h4 className="text-2xl font-bold mb-3">No Featured Engines Yet</h4>
                  <p className="text-muted-foreground mb-6">Be the first to showcase your engine</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Featured Buy Requests */}
      {!isMobile && (
        <div className="w-full px-8 lg:px-16 max-w-[2000px] mx-auto">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-amber-50/80 dark:from-amber-950/20 dark:via-orange-950/10 dark:to-amber-950/20 rounded-3xl -z-10" />
            <div className="p-8 md:p-12">
              <div className="text-center mb-10 space-y-4">
            <div className="relative flex items-center justify-center w-full">
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
              <div className="relative z-10 px-6 py-2.5 bg-background rounded-full border border-amber-200 dark:border-amber-800 shadow-sm backdrop-blur-sm">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                  Featured Buy Requests
                </h3>
              </div>
            </div>
                <p className="text-sm text-muted-foreground">Connect with buyers seeking engines</p>
              </div>
              {featuredRequests.length > 0 ? (
                <>
                  <Carousel opts={{ align: "center", loop: true, containScroll: "trimSnaps" }} className="w-full">
                    <CarouselContent className="-ml-6">
                      {featuredRequests.map((request) => (
                        <CarouselItem key={request.id} className="pl-6 basis-[360px] shrink-0">
                          <MarketplaceListingCard {...normalizeEngineForCard(request)} onClick={() => handleEngineClick(request)} onViewDetails={() => handleEngineClick(request)} />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="left-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                    <CarouselNext className="right-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                  </Carousel>
                  <div className="text-center mt-8">
                    <Link to="/marketplace?asset=engine&market=buy&type=engine_buy,engine_rent_request">
                      <Button variant="luxury" className="gap-2 shadow-lg">View All Requests<ArrowRight className="w-4 h-4" /></Button>
                    </Link>
                  </div>
                </>
              ) : (
                <div className="text-center py-10 md:py-16">
                  <Search className="w-20 h-20 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
                  <h4 className="text-2xl font-bold mb-3">No Featured Buy Requests Yet</h4>
                  <p className="text-muted-foreground mb-6">Connect with buyers</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Engine Detail Modal */}
      {selectedEngine && isModalOpen && (
        <EngineListingDetailModal
          listing={selectedEngine}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          sellerName={
            sellerProfiles[selectedEngine.user_id]?.represent_as_company && sellerProfiles[selectedEngine.user_id]?.company_name
              ? sellerProfiles[selectedEngine.user_id]?.company_name
              : (sellerProfiles[selectedEngine.user_id]?.display_name || sellerProfiles[selectedEngine.user_id]?.email)
          }
          sellerId={selectedEngine.user_id}
        />
      )}
    </section>
  );
};

export default FeaturedEngines;
